/**
 * Saved reports and the home page's numbers (crm-integrations' reports_* operations, internal/integrations/reports).
 * Every number is computed by the server in Mountain Time; the browser only shows it.
 */
import { api, downloadFile } from "./api";

export interface Stat {
  key: string;
  label: string;
  /** money (value in cents), count, percent (value is a percentage, 12.5 = 12.5%) */
  format: "money" | "count" | "percent";
  value: number;
  /** Ready to show: "$1,234.00", "1,234", "12.5%" */
  text: string;
  level?: "green" | "yellow" | "red";
}

export interface SavedReport {
  id: number;
  seed_key: string | null;
  name: string;
  description: string;
  kind: string;
  category: "sales" | "events" | "contacts" | "email" | "contact_lists" | string;
  params: Record<string, unknown>;
  window: string;
  window_label: string;
  home_group: "sales" | "company" | null;
  home_order: number;
  keap_id: number | null;
  updated_at: string;
}

export interface EventChoice {
  event_id: number;
  event_name: string;
  session_id: number;
  starts_at: string;
  upcoming: boolean;
}

export interface SavedList {
  reports: SavedReport[];
  kinds: { kind: string; label: string; description: string; category: string }[];
  windows: { preset: string; label: string }[];
  events: EventChoice[];
}

export interface Column {
  key: string;
  label: string;
  format: "text" | "money" | "count" | "date" | "datetime" | "percent";
  link?: "contact" | "order";
}

export interface RunResult {
  report_id: number;
  name: string;
  kind: string;
  window: string;
  window_label: string;
  from: string;
  to: string;
  totals: Stat[];
  columns: Column[];
  rows: Record<string, unknown>[];
  row_count: number;
  truncated: boolean;
  filters: string[];
  notes: string[];
  event?: { event_id: number; event_name: string; session_id?: number; starts_at?: string } | null;
}

export interface HomeStat {
  report_id: number;
  name: string;
  description: string;
  group: string;
  kind?: string;
  window_label: string;
  stat: Stat;
  /** Money due only: still upcoming (key "upcoming") and past due (key "past_due"); stat is the total. */
  parts?: Stat[];
  error?: string;
}

export interface EmailHealth {
  days: number;
  level: "green" | "yellow" | "red" | "none";
  summary: string;
  sent: number;
  complaint_rate: Stat;
  bounce_rate: Stat;
  engagement: Stat;
}

export interface HomeSummary {
  as_of: string;
  sales: HomeStat[];
  company: HomeStat[];
  email: { days: number; window_label: string; totals: Stat[] };
  email_health: EmailHealth;
  contacts: { days: number; tag_id?: number; tag_name?: string; new: Stat; total: Stat };
  notes: string[];
}

export interface TodoItem {
  key: string;
  label: string;
  count: number;
  link: string;
  error?: string;
}

export interface ActivityItem {
  at: string;
  kind: "open" | "click" | "form" | "order" | "payment" | "contact" | string;
  label: string;
  contact_id: number;
  name: string;
  detail: string;
}

/** Date presets offered on the Reports page (the server accepts a few more). */
export const PRESETS: { preset: string; label: string }[] = [
  { preset: "today", label: "Today" },
  { preset: "last_7_days", label: "Last 7 days" },
  { preset: "this_month", label: "This month" },
  { preset: "last_month", label: "Last month" },
  { preset: "ytd", label: "This year to date" },
  { preset: "last_year", label: "Last year" },
  { preset: "custom", label: "Custom dates" },
];

export interface RunChoices {
  /** "" = the report's own window */
  preset: string;
  from?: string;
  to?: string;
  brand_id?: number;
  /** Contact list: one of tag, segment or saved search ("tag:12", "segment:100004", "search:3"). */
  list?: string;
  session_id?: number;
  /** Money due only: "upcoming" or "past_due" ("" = both). */
  due?: string;
}

/** The request body reports_saved_run gets for the choices on screen. */
export function runRequest(c: RunChoices): Record<string, string | number> {
  const body: Record<string, string | number> = {};
  if (c.preset === "custom") {
    body.window = "custom";
    if (c.from) body.from = c.from;
    if (c.to) body.to = c.to;
  } else if (c.preset) {
    body.window = c.preset;
  }
  if (c.brand_id) body.brand_id = c.brand_id;
  if (c.due) body.due = c.due;
  const [kind, id] = (c.list ?? "").split(":");
  const n = Number(id);
  if (n > 0) {
    if (kind === "tag") body.tag_id = n;
    if (kind === "segment") body.segment_id = n;
    if (kind === "search") body.saved_search_id = n;
  }
  if (c.session_id) body.session_id = c.session_id;
  return body;
}

export const listSaved = () => api<SavedList>("/api/v1/reports/saved");

export const runSaved = (id: number, c: RunChoices) => api<RunResult>(`/api/v1/reports/saved/${id}/run`, { json: runRequest(c) });

export const exportSaved = (id: number, c: RunChoices, name: string) => downloadFile(`/api/v1/reports/saved/${id}/export`, runRequest(c), `${name}.csv`);

export const saveReport = (r: SavedReport, homeGroup: "sales" | "company" | "none") =>
  api<SavedReport>("/api/v1/reports/saved", {
    json: { id: r.id, name: r.name, description: r.description, kind: r.kind, params: r.params, window: r.window, home_group: homeGroup, home_order: r.home_order || 100 },
  });

export const homeSummary = (p: { email_days: number; contacts_days: number; contacts_tag_id?: number }) =>
  api<HomeSummary>(`/api/v1/reports/home?email_days=${p.email_days}&contacts_days=${p.contacts_days}${p.contacts_tag_id ? `&contacts_tag_id=${p.contacts_tag_id}` : ""}`);

export const todoList = () => api<{ items: TodoItem[]; total: number }>("/api/v1/reports/todo");

export const recentActivity = () => api<{ items: ActivityItem[] }>("/api/v1/reports/recent-activity?limit=12");
